/**
 * Plain text list view of the Nested Egg.
 * No styled cards — just clean text for scanning and copying.
 */
import { useState } from 'react';

export default function ListViewFormat({ tiles, mapName, measurement }) {
  const bigIdea = tiles.find((t) => t.zone === 'BIG_IDEA');
  const scaffold1 = tiles.find((t) => t.zone === 'SCAFFOLDING_1');
  const scaffold2 = tiles.find((t) => t.zone === 'SCAFFOLDING_2');
  const proof1 = tiles.filter((t) => t.zone === 'PROOF_1');
  const proof2 = tiles.filter((t) => t.zone === 'PROOF_2');
  const [copied, setCopied] = useState(false);

  function buildPlainText() {
    let text = `${mapName}\n\n`;
    if (measurement) text += `What Does Success Look Like?\n${measurement}\n\n`;
    text += `THE BIG IDEA\n${bigIdea?.text || 'Not set'}\n\n`;
    text += `SCAFFOLD 1: ${scaffold1?.text || 'Not set'}\n`;
    proof1.forEach((t) => { text += `  - ${t.text}\n`; });
    text += `\nSCAFFOLD 2: ${scaffold2?.text || 'Not set'}\n`;
    proof2.forEach((t) => { text += `  - ${t.text}\n`; });
    // Clarity Statement
    if (bigIdea && scaffold1 && scaffold2) {
      const p1 = proof1.map((t) => lf(t.text));
      const p2 = proof2.map((t) => lf(t.text));
      text += `\nYOUR CLARITY STATEMENT\n`;
      if (measurement) text += vb(measurement) ? `This is designed to ${lf(measurement)}. ` : `This delivers ${lf(measurement)}. `;
      text += `The Big Idea: ${lf(bigIdea.text)}. `;
      text += `It's built on two scaffolds: ${lf(scaffold1.text)}, and ${lf(scaffold2.text)}.`;
      const allC = [...p1, ...p2];
      if (allC.length > 0) text += ` Supporting concepts: ${allC.join(', ')}.`;
      text += '\n';
    }
    return text;
  }

  function handleCopy() {
    navigator.clipboard.writeText(buildPlainText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex-1 overflow-auto bg-white relative">
      <div className="max-w-5xl mx-auto px-8 py-5 font-body text-txt-primary">
        {/* Copy button */}
        <div className="flex justify-end mb-3">
          <button
            onClick={handleCopy}
            className="text-xs font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 border-navy/15 hover:bg-navy hover:text-white transition-colors"
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold mb-1">{mapName}</h1>

        {/* Success metric */}
        {measurement && (
          <p className="text-sm mb-4">
            <span className="font-bold uppercase">What Does Success Look Like?</span>{' '}
            {measurement}
          </p>
        )}

        {/* Big Idea */}
        <h2 className="text-xs font-bold uppercase tracking-wider text-gold-dark mt-4 mb-1">The Big Idea</h2>
        <p className="text-lg font-bold mb-4">{bigIdea?.text || 'Not set'}</p>

        {/* Scaffold 1 */}
        <h2 className="text-xs font-bold uppercase tracking-wider text-teal-dark mt-4 mb-1">Scaffold 1</h2>
        <p className="text-base font-bold mb-2">{scaffold1?.text || 'Not set'}</p>
        {proof1.length > 0 ? (
          <ul className="list-disc pl-6 space-y-1 mb-4">
            {proof1.map((t) => (
              <li key={t.id} className="text-sm">{t.text}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-txt-secondary italic mb-4">No supporting concepts</p>
        )}

        {/* Scaffold 2 */}
        <h2 className="text-xs font-bold uppercase tracking-wider text-gold-dark mt-4 mb-1">Scaffold 2</h2>
        <p className="text-base font-bold mb-2">{scaffold2?.text || 'Not set'}</p>
        {proof2.length > 0 ? (
          <ul className="list-disc pl-6 space-y-1 mb-4">
            {proof2.map((t) => (
              <li key={t.id} className="text-sm">{t.text}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-txt-secondary italic mb-4">No supporting concepts</p>
        )}
        {/* Clarity Statement */}
        {bigIdea && scaffold1 && scaffold2 && (
          <>
            <hr className="my-4 border-gold" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-gold-dark mb-1">Your Clarity Statement</h2>
            <p className="text-sm leading-relaxed">
              {measurement && (vb(measurement)
                ? <>This is designed to <strong>{lf(measurement)}</strong>. </>
                : <>This delivers <strong>{lf(measurement)}</strong>. </>)}
              The Big Idea: <strong>{lf(bigIdea.text)}</strong>.
              {' '}It's built on two scaffolds: <strong>{lf(scaffold1.text)}</strong>, and <strong>{lf(scaffold2.text)}</strong>.
              {[...proof1, ...proof2].length > 0 && <> Supporting concepts: {[...proof1, ...proof2].map((t, i, arr) => (
                <span key={t.id}>{i > 0 && i < arr.length - 1 && ', '}{i > 0 && i === arr.length - 1 && ', and '}{lf(t.text)}</span>
              ))}.</>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function lf(str) { if (!str) return ''; if (/^[A-Z]{2}/.test(str)) return str; return str.charAt(0).toLowerCase() + str.slice(1); }
function vb(str) { return /^(increase|improve|reduce|create|build|achieve|ensure|drive|get|make|deliver|grow|launch|secure|establish|develop|implement|generate|expand|maximize|minimize|optimize|strengthen|accelerate|transform|enable|empower|earn|enter|save|cut|boost|raise|lower|maintain|sustain|reach|hit|exceed|double|triple|prove|show|demonstrate|win|close|convert|retain|acquire|upsell|automate|streamline|simplify|eliminate|prevent|protect|scale|align|integrate|unify|consolidate|engage|activate|inspire|motivate|educate|train|teach|convince|persuade)\b/i.test(str); }
